import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../../shared/utils/mobile';
import { Column } from 'primereact/column';
import type { AdsAdvertiserItem, AdsPromptAd, AdsPromptItem } from '@brand24-original/llm-types';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import { SearchFilter } from '../../../shared/components/search-filter/SearchFilter';
import { AdsFilters } from '../../../shared/api/services/ads/AdsService';
import { useGetAdsPromptStats } from '../../../shared/api/hooks/ads/useGetAdsPromptStats';
import { useGetAdsPromptDetails } from '../../../shared/api/hooks/ads/useGetAdsPromptDetails';
import AdsExportButton from './AdsExportButton';
import {
  AdCopyCell,
  AdsEmpty,
  AdsErrorPanel,
  AdvertiserCell,
  AdvertiserStrip,
  ModelBadges,
  PAGINATOR_TEMPLATE,
  TableSkeleton,
  TargetUrlLink,
  useDayFormatter,
} from './AdsTableParts';

interface AdsPromptsSectionProps {
  projectId: number;
  startDate: Date | null;
  endDate: Date | null;
  filters: AdsFilters;
  /** Captured favicons per advertiser domain — reused by the advertiser strip. */
  advertisers: AdsAdvertiserItem[];
  /** monitorId → organic visibility % (existing per-monitor visibility metric). */
  visibilityByMonitor: Map<number, number>;
}

/** By-prompt row expansion: the concrete creatives detected at the prompt (fetched on expand). */
const AdsPromptExpansion: React.FC<{
  projectId: number;
  startDate: Date | null;
  endDate: Date | null;
  filters: AdsFilters;
  monitorId: number;
}> = ({ projectId, startDate, endDate, filters, monitorId }) => {
  const { t } = useTranslation();
  const formatDay = useDayFormatter();
  const { ads, isLoading, isError, refetch } = useGetAdsPromptDetails(
    projectId,
    monitorId,
    startDate,
    endDate,
    filters
  );

  if (isLoading) {
    return <TableSkeleton compact />;
  }
  if (isError) {
    return <AdsErrorPanel framed onRetry={() => void refetch()} />;
  }

  return (
    <DataTable
      stripedRows
      value={ads}
      dataKey="adKey"
      tableClassName="table"
      className="table-wrapper table-wrapper--stack"
      responsiveLayout="stack"
      tableStyle={{ tableLayout: 'fixed' }}
    >
      <Column
        header={t('ads.colAdCreative')}
        headerClassName="table__header"
        bodyClassName="table__cell table__cell--stack-title"
        body={(row: AdsPromptAd) => (
          <AdCopyCell
            title={row.title}
            body={row.body}
            imageUrl={row.imageUrl}
            faviconUrl={row.faviconUrl}
            domain={row.domain}
            advertiser={row.advertiser}
            targetUrl={row.targetUrl}
          />
        )}
      />
      <Column
        header={t('ads.colSendsTo')}
        headerClassName="table__header"
        bodyClassName="table__cell ads-page__link-cell table__cell--stack-wide"
        style={{ width: '280px' }}
        body={(row: AdsPromptAd) => (row.targetUrl ? <TargetUrlLink targetUrl={row.targetUrl} /> : '-')}
      />
      <Column
        header={t('ads.colModel')}
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell table__cell--center"
        style={{ width: '120px' }}
        body={(row: AdsPromptAd) => <ModelBadges types={row.types} />}
      />
      <Column
        header={t('ads.colAdvertiser')}
        headerClassName="table__header"
        bodyClassName="table__cell"
        style={{ width: '220px' }}
        body={(row: AdsPromptAd) => (
          <AdvertiserCell advertiser={row.advertiser} domain={row.domain} faviconUrl={row.faviconUrl} />
        )}
      />
      <Column
        field="occurrences"
        header={t('ads.colSeen')}
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell table__cell--center"
        style={{ width: '90px' }}
      />
      <Column
        header={t('ads.colFirstSeen')}
        headerClassName="table__header table__header--right"
        bodyClassName="table__cell table__cell--right"
        style={{ width: '130px' }}
        body={(row: AdsPromptAd) => formatDay(row.firstSeen)}
      />
    </DataTable>
  );
};

/**
 * "By prompt" — its own panel below the advertiser/ad breakdown (the mock keeps the prompt view out
 * of the tab switch): which monitored prompts carry ads, whose, how many, and the brand's organic
 * visibility there.
 */
const AdsPromptsSection: React.FC<AdsPromptsSectionProps> = ({
  projectId,
  startDate,
  endDate,
  filters,
  advertisers,
  visibilityByMonitor,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  // `any` matches the app-wide DataTable expansion pattern (see DomainsWithSourcesStatsTable).
  const [expandedRows, setExpandedRows] = useState<any>(null);

  const { promptStats, isLoading: isStatsLoading, isError, refetch } = useGetAdsPromptStats(
    projectId,
    startDate,
    endDate,
    filters
  );
  // Null dates = FiltersContext still hydrating (query disabled reports isLoading=false) — keep the
  // skeleton up instead of flashing the empty table.
  const isLoading = isStatsLoading || !startDate || !endDate;

  const faviconByDomain = useMemo(
    () => new Map(advertisers.map((advertiser) => [advertiser.domain, advertiser.faviconUrl])),
    [advertisers]
  );

  const normalizedSearch = search.trim().toLowerCase();
  const visiblePrompts = useMemo(
    () =>
      normalizedSearch
        ? promptStats.filter((row) => row.prompt.toLowerCase().includes(normalizedSearch))
        : promptStats,
    [promptStats, normalizedSearch]
  );

  return (
    <div className="stats__panel ads-page__prompts">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('ads.tabByPrompt')}
          <CustomTooltip tooltipText="ads.promptsTooltip" addMargin={false} />
        </div>
        {/* Tools hidden when there is nothing to search/export. */}
        {(isLoading || promptStats.length > 0) && (
          <div className="page-box__source-filter">
            <SearchFilter
              value={search}
              onChange={setSearch}
              placeholder={t('ads.searchPromptsPlaceholder')}
              className="search-filter--inline"
            />
            <AdsExportButton
              id="btn-export-ads-prompts"
              projectId={projectId}
              view="prompts"
              startDate={startDate}
              endDate={endDate}
              filters={filters}
              hasRows={promptStats.length > 0}
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <AdsErrorPanel framed onRetry={() => void refetch()} />
      ) : (
        <DataTable
          paginator
          paginatorClassName="pagination"
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          removableSort
          tableStyle={{ width: '100%' }}
          tableClassName="table"
          className="table-wrapper table-wrapper--accordion table-wrapper--stack"
          responsiveLayout="stack"
          emptyMessage={() => <AdsEmpty />}
          currentPageReportTemplate={t('rowsNumber') + ':'}
          paginatorTemplate={isMobile ? MOBILE_PAGINATOR_TEMPLATE : PAGINATOR_TEMPLATE}
          pageLinkSize={isMobile ? 3 : 5}
          value={visiblePrompts}
          dataKey="monitorId"
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={(row: AdsPromptItem) => (
            <AdsPromptExpansion
              projectId={projectId}
              startDate={startDate}
              endDate={endDate}
              filters={filters}
              monitorId={row.monitorId}
            />
          )}
        >
          <Column
            field="prompt"
            sortable
            header={t('ads.colPrompt')}
            headerClassName="table__header"
            bodyClassName="table__cell table__cell--stack-title"
          />
          <Column
            header={t('ads.colAdvertisers')}
            headerClassName="table__header"
            bodyClassName="table__cell table__cell--stack-wide"
            style={{ width: '200px' }}
            body={(row: AdsPromptItem) => (
              <AdvertiserStrip domains={row.advertiserDomains} faviconByDomain={faviconByDomain} />
            )}
          />
          <Column
            field="adOccurrences"
            sortable
            header={t('ads.colAds')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '90px' }}
          />
          <Column
            field="adCoverage"
            sortable
            header={
              isMobile ? (
                t('ads.colAdCoverage')
              ) : (
                <div className="cell-header__title">
                  <span id="ads-coverage-header">{t('ads.colAdCoverage')}</span>
                  <CustomTooltip tooltipText="ads.adCoverageTooltip" targetRef="ads-coverage-header" />
                </div>
              )
            }
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell table__cell--center"
            style={{ width: '130px' }}
            body={(row: AdsPromptItem) =>
              row.measuredAnswers > 0
                ? `${row.adCoverage}% (${row.answersWithAds}/${row.measuredAnswers})`
                : '-'
            }
          />
          <Column
            header={
              isMobile ? (
                t('ads.colVisibility')
              ) : (
                <div className="cell-header__title">
                  <span id="ads-visibility-header">{t('ads.colVisibility')}</span>
                  <CustomTooltip tooltipText="ads.visibilityTooltip" targetRef="ads-visibility-header" />
                </div>
              )
            }
            headerClassName="table__header table__header--right"
            bodyClassName="table__cell table__cell--right"
            style={{ width: '120px' }}
            body={(row: AdsPromptItem) => {
              const visibility = visibilityByMonitor.get(row.monitorId);
              return visibility !== undefined ? `${Math.round(visibility)}%` : '-';
            }}
          />
          <Column
            expander={true}
            bodyClassName="table__cell--stack-actions"
            style={{ width: '25px' }}
            frozen={!isMobile}
            alignFrozen="right"
          />
        </DataTable>
      )}
    </div>
  );
};

export default AdsPromptsSection;
